import React, { useMemo, useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';

import { getQueuesPaginated } from '../../../../redux/slices/lists/queuesSlice';
import '../zones/dropdownStyle.css';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({
  type: 'queue',
  id: row.queue_id,
  zoneName: row.zone_name ?? '',
  name: row.queue_name,
  status: row.queue_status,
});

const ListQueues: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { queues, config: headerConfig, filtersConfig, pagination, error, status, loading } = useAppSelector((s) => s.queues);

  const isLoading = loading?.getQueuesPaginated ?? false;

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'queues';
  const [showSidebar, setShowSidebar] = useState(false);

  // Initial fetch
  useEffect(() => {
    if (status === null) {
      dispatch(getQueuesPaginated({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getQueuesPaginated,
    columnFormats: { created_at: 'datetime' },
    getFormattedRow: (row: any) => {
      const rowData: any = { ...row };

      rowData.worker_names = Array.isArray(row.workers)
        ? row.workers
            .map((w: any) => w?.worker_name)
            .filter(Boolean)
            .join(', ')
        : (row.worker_name ?? '');

      rowData.connection_name = Array.isArray(row.connections)
        ? row.connections
            .map((c: any) => c?.connection_name)
            .filter(Boolean)
            .join(', ')
        : (row.connection_name ?? '');

      return rowData;
    },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh, navigate });
      return <ActionDropdown actions={actions} items={['showInstances', 'addToTransportQueue', 'open', 'close', 'edit', 'delete', 'viewAllocated']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(queues || []), [queues]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.queue_id),
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh, navigate });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(headerConfig ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createQueue" variant="primary" />
          <ActionButton actions={toolbarActions} action="addToTransportQueue" className="ms-2" variant="secondary" label="Add selected to transport" />
        </>
      }
    />
  );
};

export default ListQueues;
